import { Task, TaskMemberRole, User } from '@graphql/generated';

/**
 * Pure option-building logic for the My Tasks filter bottom sheet (TA-10187).
 * Options are derived from the tasks already loaded for the screen — no new
 * query — so the pickers only ever offer choices that can actually match.
 */

export type ProjectOption = {
  id: string;
  name: string;
  icon?: string | null;
  color?: string | null;
};

export type AssigneeOption = {
  id: string;
  name: string;
  avatar?: User['avatar'];
};

/** Unique projects across the given tasks, sorted by name for stable output. */
export const buildProjectOptions = (tasks: Task[]): ProjectOption[] => {
  const byId = new Map<string, ProjectOption>();
  tasks.forEach((task) => {
    const project = task.project;
    if (!project || byId.has(project.id)) return;
    byId.set(project.id, {
      id: project.id,
      name: project.name ?? 'Unknown Project',
      icon: project.icon,
      color: project.color,
    });
  });
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/** Unique assignees (Assignee role only) across the given tasks. */
export const buildAssigneeOptions = (tasks: Task[]): AssigneeOption[] => {
  const byId = new Map<string, AssigneeOption>();
  tasks.forEach((task) => {
    task.members?.forEach((member) => {
      if (member.role !== TaskMemberRole.Assignee) return;
      const user = member.user;
      if (!user || byId.has(user.id)) return;
      byId.set(user.id, {
        id: user.id,
        name: user.name ?? 'Unknown',
        avatar: user.avatar ?? null,
      });
    });
  });
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Previously-selected options float to the top when a picker reopens (AC4).
 * Relative order inside each partition is preserved.
 */
export const sortSelectedFirst = <T extends { id: string }>(
  options: T[],
  selectedIds: string[]
): T[] => {
  const selected = new Set(selectedIds);
  return [
    ...options.filter((o) => selected.has(o.id)),
    ...options.filter((o) => !selected.has(o.id)),
  ];
};

/** Case-insensitive name search for the assignee picker (AC3). */
export const filterOptionsByName = <T extends { name: string }>(
  options: T[],
  term: string
): T[] => {
  const query = term.trim().toLowerCase();
  if (!query) return options;
  return options.filter((o) => o.name.toLowerCase().includes(query));
};
